import {
  AppFilters,
  AuditLogFilterOptionsResponse,
  AuditLogListResponse,
  ComplianceCoverageFileType,
  ComplianceCoverageResponse,
  ComplianceDocument,
  Config,
  FileType,
  FolderStructureResponse,
  GameSchema,
  MarketingAssetFileDetailSchema,
  MarketingAssetSchema,
  MarketSchema,
  SharedBasketCreateRequest,
  SharedBasketCreateResponse,
  SharedBasketItemInput,
  SharedBasketPreviewResponse,
  SharedBasketSummary,
  SharedBasketViewResponse,
  UnifiedSearchParams,
  UnifiedSearchResponse,
  UserSchema,
  type PartnerSchema,
} from './types';
import axiosInstance from './axios';
import config from '@/mocks/config.json';
import { parseUrlParams } from './urlParams';
import {
  saveDownloadedFiles,
  DownloadSignedUrlFile,
  DownloadSignedUrlsResponse,
  DownloadJobPreparingResponse,
  DownloadParams,
  DownloadProgressCallback,
} from './download';

export const fetchUnifiedSearch = (
  params: UnifiedSearchParams,
): Promise<UnifiedSearchResponse> =>
  axiosInstance(`/api/search?${parseUrlParams(params)}`).then(
    (response) => response.data,
  );

export const fetchPartners = (params?: {
  type?: string[];
  market_ids?: number[];
  search?: string;
  platform_names?: string[];
  operator_names?: string[];
  brand_names?: string[];
  partner_ids?: number[];
  offset?: number;
  limit?: number;
}): Promise<PartnerSchema[]> =>
  axiosInstance(`/api/partners?${parseUrlParams(params)}`).then(
    (response) => response.data,
  );

export const fetchMarketingAssets = (params?: {
  offset?: number;
  limit?: number;
  languages?: string[];
  file_types?: string[];
  game_ids?: number[];
  game_group?: string[];
  partner_ids?: number[];
  extensions?: string[];
  sort_by?: string;
  sort_order?: string;
}): Promise<MarketingAssetSchema[]> => {
  return axiosInstance(
    `/api/marketing/assets?${parseUrlParams(params)}`,
  ).then((response) => response.data);
};

const ALL_MARKETING_ASSETS_PAGE_SIZE = 100;

// Every asset matching the filter, not just the pages on screen - the
// list endpoint has no total, so page until a short page comes back.
export const fetchAllMarketingAssets = async (
  params: Omit<
    NonNullable<Parameters<typeof fetchMarketingAssets>[0]>,
    'offset' | 'limit'
  >,
): Promise<MarketingAssetSchema[]> => {
  const assets: MarketingAssetSchema[] = [];
  for (let offset = 0; ; offset += ALL_MARKETING_ASSETS_PAGE_SIZE) {
    const page = await fetchMarketingAssets({
      ...params,
      offset,
      limit: ALL_MARKETING_ASSETS_PAGE_SIZE,
    });
    assets.push(...page);
    if (page.length < ALL_MARKETING_ASSETS_PAGE_SIZE) return assets;
  }
};

export const fetchMarketingAssetsFolderStructure = (params?: {
  game_ids?: number[];
  game_group?: string[];
}): Promise<FolderStructureResponse> =>
  axiosInstance(
    `/api/marketing/assets/folder-structure?${parseUrlParams(
      params,
    )}`,
  ).then((response) => response.data);

export const fetchMarketingAssetsByFolder = (params: {
  folder_path: string;
  languages?: string[];
  file_types?: string[];
  file_groups?: string[];
  game_ids?: number[];
  game_group?: string[];
  partner_ids?: number[];
  search?: string;
  extensions?: string[];
  sort_by?: string;
  sort_order?: string;
  offset?: number;
  limit?: number;
}): Promise<MarketingAssetFileDetailSchema[]> =>
  axiosInstance(
    `/api/marketing/assets/by-folder?${parseUrlParams(params)}`,
  ).then((response) => response.data);

export const fetchUser = (): Promise<UserSchema> => {
  return axiosInstance(`/api/me`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

// Public endpoint: an invitation link is opened before the user has an
// account, so this must not depend on a token. The login page renders its
// own error state, hence no global 404 toast.
export const fetchInvitationStatus = (
  invitation: string,
): Promise<{ valid: boolean }> =>
  axiosInstance(`/api/invitation-status`, {
    params: { invitation },
    skipGlobalNotFound: true,
  }).then((response) => response.data);

export const fetchConfig = (): Promise<Config> =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(config as Config);
    }, 500);
  });

export const fetchPartnersMarkets = (
  partner_id: string,
): Promise<MarketSchema[]> =>
  axiosInstance(`/api/markets`, {
    params: { partner_id },
  }).then((response) => response.data);

export const postMarketsDetach = (
  market_id: number,
  partner_id: number | string,
) =>
  axiosInstance(`/api/markets/${market_id}/detach`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({ partner_id }),
  });

export const fetchComplianceDocuments = (params?: {
  offset?: number;
  limit?: number;
  document_types?: string[];
  market_ids?: number[];
  partner_ids?: number[];
  game_ids?: number[];
  languages?: string[];
  extensions?: string[];
  sort_by?: string;
  sort_order?: string;
}): Promise<ComplianceDocument[]> =>
  axiosInstance(
    `/api/compliance/documents?${parseUrlParams(params)}`,
  ).then((response) => response.data);

// Documents whose certificate code is one of `codes` (whole code, any
// letter case). Returns [] when nothing matches.
export const fetchComplianceDocumentsByCode = (params: {
  codes: string[];
  document_type?: string;
}): Promise<ComplianceDocument[]> =>
  axiosInstance(
    `/api/compliance/documents/by-code?${parseUrlParams(params)}`,
  ).then((response) => response.data);

export const fetchComplianceDocumentsFolderStructure = (params?: {
  game_ids?: number[];
  market_ids?: number[];
}): Promise<FolderStructureResponse> =>
  axiosInstance(
    `/api/compliance/documents/folder-structure?${parseUrlParams(
      params,
    )}`,
  ).then((response) => response.data);

export const fetchComplianceDocumentsByFolder = (params: {
  folder_path: string;
  document_types?: string[];
  market_ids?: number[];
  partner_ids?: number[];
  game_ids?: number[];
  languages?: string[];
  extensions?: string[];
  sort_by?: string;
  sort_order?: string;
  offset?: number;
  limit?: number;
}): Promise<ComplianceDocument[]> =>
  axiosInstance(
    `/api/compliance/documents/by-folder?${parseUrlParams(params)}`,
  ).then((response) => response.data);

export const fetchFilters = (): Promise<AppFilters> =>
  axiosInstance(`/api/filters`).then((response) => response.data);

export type PostPartnersLeadParams = {
  name: string;
  type: string;
  selectedMarkets: number[];
  selectedPlatformProvider: string | null;
  parent_id: number | null;
};

export const postPartnersLead = ({
  name,
  type,
  selectedMarkets,
  selectedPlatformProvider,
  parent_id,
}: PostPartnersLeadParams) =>
  axiosInstance(`/api/partners/partner-lead`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({
      name,
      type,
      status: 'active',
      market_ids: selectedMarkets,
      platform_provider: selectedPlatformProvider,
      platform_id: parent_id,
    }),
  });

export type PostMarketsParams = {
  name: string;
  description: string;
};

export const postMarkets = ({
  name,
  description,
}: PostMarketsParams) =>
  axiosInstance(`/api/markets`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({
      name,
      description,
    }),
  });

export const fetchGames = (params: {
  offset?: number;
  limit?: number;
  partner_ids?: string[];
  market_ids?: number[];
  game_ids?: number[];
  game_status?: string[];
  game_name?: string;
  volatility?: string;
  rtp?: string[];
  release_date_from?: string;
  release_date_to?: string;
  game_family?: string[];
  game_group?: string[];
  game_features?: string[];
  game_provider?: string[];
  languages?: string[];
  sort_by?: string;
  sort_order?: string;
}): Promise<{ games: GameSchema[]; total_count: number }> => {
  return axiosInstance(`/api/games?${parseUrlParams(params)}`).then(
    (response) => response.data,
  );
};

export type GamesCalendarResponse = {
  year: number;
  month: number;
  dates: {
    date: string;
    amount: number;
  }[];
  total_count: number;
};

export const fetchGamesCalendar = (params?: {
  year?: number | null;
  month?: number | null;
}): Promise<GamesCalendarResponse> => {
  const queryParams:
    | Record<string, string | number | number[] | string[]>
    | undefined = params
    ? {
        ...(params.year != null && { year: params.year }),
        ...(params.month != null && { month: params.month }),
      }
    : undefined;

  return axiosInstance(
    `/api/games/calendar?${parseUrlParams(queryParams)}`,
  ).then((response) => response.data);
};

export const fetchGamesGroupedByGameGroup = (params: {
  offset?: number;
  limit?: number;
  partner_ids?: string[];
  market_ids?: number[];
  game_status?: string[];
  game_name?: string;
  volatility?: string;
  rtp?: string[];
  release_date_from?: string;
  release_date_to?: string;
  game_family?: string[];
  game_group?: string[];
  game_features?: string[];
  game_provider?: string[];
  languages?: string[];
  sort_by?: string;
  sort_order?: string;
}): Promise<{
  games_by_group: { game_group: string; games: GameSchema[] }[];
  total_count: number;
}> => {
  return axiosInstance(
    `/api/games/grouped-by-game-group?${parseUrlParams(params)}`,
  ).then((response) => response.data);
};

export const fetchGamesAll = (params: {
  offset?: number;
  limit?: number;
  partner_ids?: string[];
  market_ids?: number[];
  game_status?: string[];
  game_name?: string;
  volatility?: string;
  rtp?: string[];
  release_date_from?: string;
  release_date_to?: string;
  game_family?: string[];
  game_group?: string[];
  game_features?: string[];
  game_provider?: string[];
  languages?: string[];
  sort_by?: string;
  sort_order?: string;
}): Promise<{ games: GameSchema[] }> => {
  return axiosInstance(
    `/api/games/all?${parseUrlParams(params)}`,
  ).then((response) => response.data);
};

export const fetchGamesExport = (params: {
  q?: string;
  partner_ids?: string[];
  market_ids?: number[];
  game_status?: string[];
  volatility_from?: number;
  volatility_to?: number;
  rtp_from?: string;
  rtp_to?: string;
  release_date_from?: string;
  release_date_to?: string;
  game_family?: string[];
  game_group?: string[];
  game_features?: string[];
  game_provider?: string[];
  languages?: string[];
  sort_by?: string;
  sort_order?: string;
}): Promise<Blob> => {
  return axiosInstance(
    `/api/games/export?${parseUrlParams(params)}`,
    { responseType: 'blob' },
  ).then((response) => response.data);
};

export const fetchGameById = (gameId: number): Promise<GameSchema> =>
  axiosInstance(`/api/games/${gameId}`).then(
    (response) => response.data,
  );

export const postMarketsAttach = ({
  market_id,
  partner_id,
}: {
  market_id: number;
  partner_id: number;
}) =>
  axiosInstance(`/api/markets/${market_id}/attach`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({
      partner_id,
    }),
  });

const DOWNLOAD_JOB_POLL_INTERVAL_MS = 2000;
// Matches how long the backend holds a build before treating it as dead.
const DOWNLOAD_JOB_MAX_WAIT_MS = 15 * 60 * 1000;

// Polls a server-side zip build until it's ready. A 404 (job expired) or
// 500 (build failed) rejects, which the caller shows as a failed
// download whose retry re-sends the whole request.
const waitForDownloadJob = async (
  jobId: string,
): Promise<DownloadSignedUrlsResponse> => {
  const deadline = Date.now() + DOWNLOAD_JOB_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) =>
      setTimeout(resolve, DOWNLOAD_JOB_POLL_INTERVAL_MS),
    );
    const res = await axiosInstance.get<
      DownloadSignedUrlsResponse | DownloadJobPreparingResponse
    >(`/api/download/jobs/${encodeURIComponent(jobId)}`);
    if (res.status === 200) {
      return res.data as DownloadSignedUrlsResponse;
    }
  }
  throw new Error('Download failed: preparing the zip timed out');
};

export const postDownload = (
  data: DownloadParams,
  onProgress?: DownloadProgressCallback,
  onPreparing?: () => void,
): Promise<DownloadSignedUrlFile[]> => {
  const { game_name, ...apiData } = data;
  const filteredFileIds = apiData.file_ids?.filter((id) =>
    Number.isInteger(id),
  );

  return axiosInstance
    .post<DownloadSignedUrlsResponse | DownloadJobPreparingResponse>(
      `/api/download`,
      {
        ...apiData,
        file_ids: filteredFileIds,
      },
    )
    .then(async (res) => {
      let payload = res.data as DownloadSignedUrlsResponse;
      if (res.status === 202) {
        onPreparing?.();
        payload = await waitForDownloadJob(
          (res.data as DownloadJobPreparingResponse).job_id,
        );
      }
      const files = payload?.files;
      if (!Array.isArray(files) || files.length === 0) {
        throw new Error('Download failed: no files returned');
      }
      return saveDownloadedFiles(
        files,
        {
          ...apiData,
          file_ids: filteredFileIds,
          game_name,
        },
        onProgress,
      );
    });
};

// The server now batches its access-control lookup into one query per
// category, so a basket costs roughly the same whatever its size. This still
// sits well above the instance-wide 3s timeout because create also writes the
// basket and its recipients, and the basket is uncapped.
const SHARED_BASKET_CREATE_TIMEOUT_MS = 30_000;

// A full-portal workbook can take longer than the 30s instance default.
export const COMPLIANCE_COVERAGE_EXPORT_TIMEOUT_MS = 120_000;

export const postSharedBasket = (
  data: SharedBasketCreateRequest,
): Promise<SharedBasketCreateResponse> => {
  const { partner_id, contact_user_ids, ...rest } = data;

  // Sending the field that doesn't match the mode is a 422.
  const recipients =
    rest.recipient_mode === 'partner'
      ? { partner_id }
      : { contact_user_ids };

  return axiosInstance
    .post<SharedBasketCreateResponse>(
      `/api/shared-baskets`,
      { ...rest, ...recipients },
      { timeout: SHARED_BASKET_CREATE_TIMEOUT_MS },
    )
    .then((response) => response.data);
};

export const fetchSharedBaskets = (): Promise<
  SharedBasketSummary[]
> =>
  axiosInstance(`/api/shared-baskets`).then(
    (response) => response.data,
  );

export const fetchSharedBasket = (
  token: string,
): Promise<SharedBasketViewResponse> =>
  axiosInstance(`/api/shared-baskets/${token}`, {
    skipGlobalNotFound: true,
  }).then((response) => response.data);

export const postSharedBasketDownload = (
  token: string,
  items?: SharedBasketItemInput[],
  onProgress?: DownloadProgressCallback,
): Promise<DownloadSignedUrlFile[]> => {
  const filteredItems = items?.filter((item) =>
    Number.isInteger(item?.file_id),
  );

  return axiosInstance
    .post<DownloadSignedUrlsResponse>(
      `/api/shared-baskets/${token}/download`,
      // Omitting items means every file the viewer can access.
      filteredItems ? { items: filteredItems } : {},
      { skipGlobalNotFound: true },
    )
    .then((res) => {
      const files = res.data?.files;
      if (!Array.isArray(files) || files.length === 0) {
        throw new Error('Download failed: no files returned');
      }
      // A basket can hold both categories, so there is no single one to
      // report; entity_name is what names the zip.
      return saveDownloadedFiles(
        files,
        { category: 'all', entity_name: 'Shared Basket' },
        onProgress,
      );
    });
};

export const postSharedBasketPreview = (
  items: SharedBasketItemInput[],
): Promise<SharedBasketPreviewResponse> =>
  axiosInstance
    .post<SharedBasketPreviewResponse>(
      `/api/shared-baskets/preview`,
      { items },
      { timeout: SHARED_BASKET_CREATE_TIMEOUT_MS },
    )
    .then((response) => response.data);

export const postSharedBasketDisable = (
  token: string,
): Promise<void> =>
  axiosInstance(`/api/shared-baskets/${token}/disable`, {
    method: 'POST',
  }).then(() => undefined);

export const deleteComplianceDocument = (document_id: number) =>
  axiosInstance(`/api/compliance/documents/${document_id}`, {
    method: 'DELETE',
  });

export type ComplianceUploadSessionFileInput = {
  name: string;
  document_type: FileType;
  size_bytes: number;
  extension: string;
  mime_type: string;
  game_ids: number[];
  market_ids: number[];
  partner_ids: number[];
  language?: string;
  code?: string | null;
};

export type ComplianceUploadSessionResult = {
  index: number;
  file_id?: number;
  path?: string;
  upload_url?: string;
  filename?: string;
  error?: string;
};

export type PostComplianceDocumentsUploadSessionsResponse = {
  results: ComplianceUploadSessionResult[];
};

export const postComplianceDocumentsUploadSessions = (params: {
  files: ComplianceUploadSessionFileInput[];
}): Promise<PostComplianceDocumentsUploadSessionsResponse> =>
  axiosInstance<PostComplianceDocumentsUploadSessionsResponse>(
    `/api/compliance/documents/upload-sessions`,
    {
      method: 'POST',
      data: params,
    },
  ).then((response) => response.data);

export type ComplianceDocumentsPrecheckFileInput = {
  name: string;
  extension: string;
  document_type: FileType;
  game_ids: number[];
  market_ids: number[];
  partner_ids: number[];
};

export type UploadPrecheckResult = {
  index: number;
  filename: string;
  valid: boolean;
  error?: string;
  replaces_existing?: boolean;
};

export type PostComplianceDocumentsPrecheckResponse = {
  results: UploadPrecheckResult[];
};

export const postComplianceDocumentsPrecheck = (params: {
  files: ComplianceDocumentsPrecheckFileInput[];
}): Promise<PostComplianceDocumentsPrecheckResponse> =>
  axiosInstance<PostComplianceDocumentsPrecheckResponse>(
    `/api/compliance/documents/precheck`,
    {
      method: 'POST',
      data: params,
    },
  ).then((response) => response.data);

export const postComplianceDocumentsUploadAbandon = (params: {
  file_ids: number[];
}): Promise<PostUploadAbandonResponse> =>
  axiosInstance<PostUploadAbandonResponse>(
    `/api/compliance/documents/upload/abandon`,
    {
      method: 'POST',
      data: params,
    },
  ).then((response) => response.data);

// `crc32` is keyed by file id; see lib/crc32.ts for why the backend wants it.
export type UploadCompleteParams = {
  file_ids: number[];
  crc32?: Record<number, string>;
};

export const postComplianceDocumentsUploadComplete = (
  params: UploadCompleteParams,
): Promise<unknown> =>
  axiosInstance(`/api/compliance/documents/upload/complete`, {
    method: 'POST',
    data: params,
  }).then((response) => response.data);

export type ComplianceUploadSessionRefreshResult = {
  file_id: number;
  upload_url?: string;
  error?: string;
};

export type PostComplianceDocumentsUploadSessionResponse = {
  results: ComplianceUploadSessionRefreshResult[];
};

export const postComplianceDocumentsUploadSession = (params: {
  file_ids: number[];
}): Promise<PostComplianceDocumentsUploadSessionResponse> =>
  axiosInstance<PostComplianceDocumentsUploadSessionResponse>(
    `/api/compliance/documents/upload/session`,
    {
      method: 'POST',
      data: params,
    },
  ).then((response) => response.data);

export type MarketingUploadSessionFileInput = {
  name: string;
  file_type: FileType;
  size_bytes: number;
  extension: string;
  mime_type: string;
  game_ids: number[];
  partner_ids: number[];
};

export type MarketingUploadSessionResult = {
  index: number;
  file_id?: number;
  path?: string;
  upload_url?: string;
  filename?: string;
  error?: string;
};

export type PostMarketingAssetsUploadSessionsResponse = {
  results: MarketingUploadSessionResult[];
};

export const postMarketingAssetsUploadSessions = (params: {
  files: MarketingUploadSessionFileInput[];
}): Promise<PostMarketingAssetsUploadSessionsResponse> =>
  axiosInstance<PostMarketingAssetsUploadSessionsResponse>(
    `/api/marketing/assets/upload-sessions`,
    {
      method: 'POST',
      data: params,
    },
  ).then((response) => response.data);

export type MarketingAssetsPrecheckFileInput = {
  name: string;
  extension: string;
  file_type: FileType;
  game_ids: number[];
  partner_ids: number[];
};

export type PostMarketingAssetsPrecheckResponse = {
  results: UploadPrecheckResult[];
};

export const postMarketingAssetsPrecheck = (params: {
  files: MarketingAssetsPrecheckFileInput[];
}): Promise<PostMarketingAssetsPrecheckResponse> =>
  axiosInstance<PostMarketingAssetsPrecheckResponse>(
    `/api/marketing/assets/precheck`,
    {
      method: 'POST',
      data: params,
    },
  ).then((response) => response.data);

export const postMarketingAssetsUploadComplete = (
  params: UploadCompleteParams,
): Promise<unknown> =>
  axiosInstance(`/api/marketing/assets/upload/complete`, {
    method: 'POST',
    data: params,
  }).then((response) => response.data);

export type AbandonUploadResult = {
  file_id: number;
  status?: string;
  error?: string;
};

export type PostUploadAbandonResponse = {
  results: AbandonUploadResult[];
};

export const postMarketingAssetsUploadAbandon = (params: {
  file_ids: number[];
}): Promise<PostUploadAbandonResponse> =>
  axiosInstance<PostUploadAbandonResponse>(
    `/api/marketing/assets/upload/abandon`,
    {
      method: 'POST',
      data: params,
    },
  ).then((response) => response.data);

export type MarketingUploadSessionRefreshResult = {
  file_id: number;
  upload_url?: string;
  error?: string;
};

export type PostMarketingAssetsUploadSessionResponse = {
  results: MarketingUploadSessionRefreshResult[];
};

export const postMarketingAssetsUploadSession = (params: {
  file_ids: number[];
}): Promise<PostMarketingAssetsUploadSessionResponse> =>
  axiosInstance<PostMarketingAssetsUploadSessionResponse>(
    `/api/marketing/assets/upload/session`,
    {
      method: 'POST',
      data: params,
    },
  ).then((response) => response.data);

export type DeleteMarketingAssetsResponse = {
  deleted_ids: number[];
  skipped_ids: number[];
};

export const deleteMarketingAssets = (
  file_ids: number[],
): Promise<DeleteMarketingAssetsResponse> =>
  axiosInstance<DeleteMarketingAssetsResponse>(
    `/api/marketing/assets/`,
    {
      method: 'DELETE',
      data: { file_ids },
    },
  ).then((response) => response.data);

export const postGamesAttachPartner = (
  game_id: number,
  partner_id: number,
) =>
  axiosInstance(`/api/games/${game_id}/attach-partner`, {
    method: 'POST',
    data: { partner_id },
  });

export const postGamesDetachPartner = (
  game_id: number,
  partner_id: number,
) =>
  axiosInstance(`/api/games/${game_id}/detach-partner`, {
    method: 'POST',
    data: { partner_id },
  });

export const postGamesThumbnail = (
  game_id: number,
  marketing_file_id: number,
) =>
  axiosInstance.postForm(`/api/games/${game_id}/photo`, {
    marketing_file_id,
  });

export const postInvite = ({
  name,
  email,
  role_name,
  partner_ids,
  permission_ids,
}: {
  name: string;
  email: string;
  role_name?: string;
  partner_ids: number[];
  permission_ids?: number[];
}) =>
  axiosInstance(`/api/invite`, {
    method: 'POST',
    data: {
      name,
      email,
      role_name,
      partner_ids,
      permission_ids,
    },
  });

export const postBatchInvite = (data: {
  invitations: {
    email: string;
    name: string;
    role_name?: string;
    partner_id: number;
    permission_ids?: number[];
  }[];
}) =>
  axiosInstance<{
    total: number;
    failed: number;
    successful: number;
    successes: {
      user: {
        id: number;
        name: string;
        email: string;
        is_active: boolean;
      };
      email: string;
    }[];
    failures: {
      email: string;
      name: string;
      error: string;
      error_type: string;
      role_name: string;
      partner_id: number;
    }[];
    email: string;
  }>(`/api/batch-invite`, {
    method: 'POST',
    data,
  });

export const fetchPartnersUsersByPartnerId = (partner_id: number) =>
  axiosInstance(`/api/partners/${partner_id}/users`).then(
    (response) => response.data,
  );

export const deletePartnersUsersByPartnerId = (
  partner_id: number,
  user_ids: number[],
) =>
  axiosInstance(`/api/partners/${partner_id}/users`, {
    method: 'DELETE',
    data: { user_ids },
  });

export const postResendInvitation = (user_id: number) =>
  axiosInstance<{ message: string }>(
    `/api/users/${user_id}/resend-invitation`,
    { method: 'POST' },
  );

export const deleteUserByUserId = (user_id: number) =>
  axiosInstance(`/api/users/${user_id}`, {
    method: 'DELETE',
  });

export const putUser = ({
  user_id,
  name,
  partner_ids,
}: {
  user_id: number;
  name: string;
  partner_ids?: number[];
}) =>
  axiosInstance(`/api/users/${user_id}`, {
    method: 'PUT',
    data: {
      name,
      is_active: true,
      ...(partner_ids !== undefined && { partner_ids }),
    },
  });

export const postUserRoles = ({
  user_id,
  role_id,
}: {
  user_id: number;
  role_id: number;
}) =>
  axiosInstance(`/api/users/${user_id}/roles`, {
    method: 'POST',
    data: { role_id },
  });

export const deleteUserRoles = ({
  user_id,
  role_id,
}: {
  user_id: number;
  role_id: number;
}) =>
  axiosInstance(`/api/users/${user_id}/roles`, {
    method: 'DELETE',
    data: { role_id },
  });

export const postUserPermissions = ({
  user_id,
  permission_ids,
}: {
  user_id: number;
  permission_ids: number[];
}) =>
  axiosInstance(`/api/users/${user_id}/permissions`, {
    method: 'POST',
    data: { permission_ids },
  });

export const deleteUserPermissions = ({
  user_id,
  permission_ids,
}: {
  user_id: number;
  permission_ids: number[];
}) =>
  axiosInstance(`/api/users/${user_id}/permissions`, {
    method: 'DELETE',
    data: { permission_ids },
  });

export type PostFeedbackParams = {
  feedback_type: string;
  message: string;
  name?: string | null;
  email?: string | null;
  title?: string | null;
  attachments?: File[];
};

export const postFeedback = (data: PostFeedbackParams) =>
  axiosInstance.postForm(`/api/feedback`, data, {
    formSerializer: { indexes: null },
  });

export const fetchAuditLog = (params?: {
  offset?: number;
  limit?: number;
  search?: string;
  entity_types?: string[];
  actions?: string[];
}): Promise<AuditLogListResponse> =>
  axiosInstance(`/api/audit-log?${parseUrlParams(params)}`).then(
    (response) => response.data,
  );

export const fetchComplianceCoverage = (params: {
  offset: number;
  limit: number;
  document_type: ComplianceCoverageFileType;
  search?: string;
  market_id?: number;
}): Promise<ComplianceCoverageResponse> =>
  axiosInstance(
    `/api/compliance/coverage?${parseUrlParams(params)}`,
  ).then((response) => response.data);

export const fetchComplianceCoverageExport = (params: {
  search?: string;
  market_id?: number;
}): Promise<Blob> =>
  axiosInstance(
    `/api/compliance/coverage/export?${parseUrlParams(params)}`,
    {
      responseType: 'blob',
      timeout: COMPLIANCE_COVERAGE_EXPORT_TIMEOUT_MS,
    },
  ).then((response) => response.data);

export const fetchAuditLogFilterOptions =
  (): Promise<AuditLogFilterOptionsResponse> =>
    axiosInstance(`/api/audit-log/filter-options`).then(
      (response) => response.data,
    );
